import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  fetchNotificationFeed,
  fetchNotReadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationFeed,
  type NotificationFeedFilters,
} from "@/api/notifications";

/**
 * D-043 S2 — notification hooks.
 *
 * Keys:
 *   ['notifications','count']                     — badge, 30s poll (stock notReadCount)
 *   ['notifications','dropdown']                  — bell dropdown, lazy on open
 *   ['notifications','center', filters]           — center list, filters in key
 *
 * Reading lists NEVER mutates read state (custom no-auto-mark endpoint);
 * read state changes only через explicit optimistic mutations below.
 */

const COUNT_KEY = ["notifications", "count"] as const;
const DROPDOWN_KEY = ["notifications", "dropdown"] as const;
const DROPDOWN_LIMIT = 8;

export function useNotificationsCount(enabled = true) {
  return useQuery({
    queryKey: COUNT_KEY,
    queryFn: ({ signal }) => fetchNotReadCount(signal),
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });
}

export function useNotificationDropdown(open: boolean) {
  return useQuery({
    queryKey: DROPDOWN_KEY,
    queryFn: ({ signal }) =>
      fetchNotificationFeed({ view: "all", limit: DROPDOWN_LIMIT }, signal),
    enabled: open,
    staleTime: 15_000,
  });
}

export function useNotificationCenter(filters: NotificationFeedFilters) {
  return useQuery({
    queryKey: ["notifications", "center", filters],
    queryFn: ({ signal }) => fetchNotificationFeed(filters, signal),
    staleTime: 30_000,
    placeholderData: (prev) => prev, // keep list during tab/filter switch
  });
}

/* ------------------------------------------------ optimistic mutations */

interface FeedSnapshot {
  count: number | undefined;
  feeds: [readonly unknown[], NotificationFeed | undefined][];
}

function snapshotFeeds(qc: QueryClient): FeedSnapshot {
  return {
    count: qc.getQueryData<number>(COUNT_KEY),
    feeds: qc
      .getQueriesData<NotificationFeed>({ queryKey: ["notifications"] })
      .filter(([key]) => key[1] === "dropdown" || key[1] === "center"),
  };
}

function restoreFeeds(qc: QueryClient, snap: FeedSnapshot) {
  if (snap.count !== undefined) qc.setQueryData(COUNT_KEY, snap.count);
  for (const [key, data] of snap.feeds) {
    if (data !== undefined) qc.setQueryData(key, data);
  }
}

function patchFeeds(
  qc: QueryClient,
  patch: (feed: NotificationFeed) => NotificationFeed,
) {
  const entries = qc.getQueriesData<NotificationFeed>({ queryKey: ["notifications"] });
  for (const [key, data] of entries) {
    if ((key[1] === "dropdown" || key[1] === "center") && data) {
      qc.setQueryData(key, patch(data));
    }
  }
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["notifications"] });
      const snap = snapshotFeeds(qc);

      let wasUnread = false;
      patchFeeds(qc, (feed) => ({
        ...feed,
        list: feed.list.map((row) => {
          if (row.id === id && !row.read) {
            wasUnread = true;
            return { ...row, read: true };
          }
          return row;
        }),
        unreadTotal: Math.max(
          0,
          feed.unreadTotal - (feed.list.some((r) => r.id === id && !r.read) ? 1 : 0),
        ),
      }));

      if (wasUnread) {
        qc.setQueryData<number>(COUNT_KEY, (c) => Math.max(0, (c ?? 1) - 1));
      }

      return snap;
    },
    onError: (_e, _id, snap) => snap && restoreFeeds(qc, snap),
    onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["notifications"] });
      const snap = snapshotFeeds(qc);

      patchFeeds(qc, (feed) => ({
        ...feed,
        unreadTotal: 0,
        list: feed.list.map((row) => (row.read ? row : { ...row, read: true })),
      }));
      qc.setQueryData(COUNT_KEY, 0);

      return snap;
    },
    onError: (_e, _v, snap) => snap && restoreFeeds(qc, snap),
    onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useInvalidateNotifications() {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    [qc],
  );
}
